import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import InputLabel from '@mui/material/InputLabel';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AgeSelect } from '../../../../components/inputs/AgeSelect';
import { SingleSelect } from '../../../../components/inputs/SingleSelect';
import {
  GIFT_AMOUNTS,
  STATE_CODE_TO_FIPS,
  US_STATES,
} from '../../../../config/dropdowns';
import { mergeSx } from '../../../../theme';

import { searchBarStyles as styles } from './SearchBar.styles';

/**
 * Impact filter card — Age / Amount / Location + "View my impact" CTA.
 * Figma: 59:24344 — 1060×108, cornerRadius 8.
 *
 * Submitting navigates to the impact results screen, carrying the chosen
 * filters as query params. (The results screen will use these to seed the
 * API call for real backend data.)
 */
export function SearchBar() {
  const navigate = useNavigate();
  const [ageIds, setAgeIds] = useState<string[]>([]);
  const [amount, setAmount] = useState('');
  const [location, setLocation] = useState('');

  const stateOptions = useMemo(
    () =>
      [...US_STATES]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((state) => ({ id: state.code, label: state.name })),
    [],
  );

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const params = new URLSearchParams();
    if (ageIds.length) params.set('age', ageIds.join(','));
    if (amount) params.set('amount', amount);
    // Identify the state by FIPS in the URL (canonical id for the impact map).
    if (location) params.set('location', STATE_CODE_TO_FIPS[location] ?? location);
    const query = params.toString();
    navigate(query ? `/impact?${query}` : '/impact');
  };

  return (
    <Box sx={styles.wrapper}>
      <Box component="form" role="search" sx={styles.root} onSubmit={handleSubmit}>
        <Box sx={styles.field}>
          <InputLabel htmlFor="age-range">Age</InputLabel>
          <AgeSelect
            id="age-range"
            value={ageIds}
            onChange={setAgeIds}
            displayEmpty
            placeholder="Select range"
          />
        </Box>
        <Box sx={styles.field}>
          <InputLabel htmlFor="amount">Amount</InputLabel>
          <SingleSelect
            id="amount"
            value={amount}
            onChange={setAmount}
            options={GIFT_AMOUNTS}
            displayEmpty
            showPlaceholderOption
            placeholder="Select amount"
            variant="standard"
          />
        </Box>
        <Box sx={mergeSx(styles.field, styles.fieldLast)}>
          <InputLabel htmlFor="location">Location</InputLabel>
          <SingleSelect
            id="location"
            value={location}
            onChange={setLocation}
            options={stateOptions}
            displayEmpty
            showPlaceholderOption
            placeholder="Select area"
            variant="standard"
          />
        </Box>
        <Box sx={styles.cta}>
          <Button type="submit" variant="outlined" color="primary" sx={styles.ctaButton}>
            View my impact
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
